<template>
    <div class="vfb-field vfb-field--file" :class="classes.wrapper">
        <div class="vfb-field__container" :class="classes.container">
            <FormLabel :id="id" :label="label" :required="required" :className="classes.label" />

            <input
                class="vfb-file-input form-control"
                :id="id"
                :name="submitName"
                :class="classes.input"
                type="file"
                :required="required"
                :disabled="disabled"
                :multiple="multiple"
                :accept="acceptTypes || undefined"
                :aria-describedby="helpText ? id + '-help-block' : undefined"
                @change="onChange"
                :data-validation-rules="validationRules"
            />

            <HelpText v-if="helpText" :helpText="helpText" :id="id + '-help-block'" :className="classes.help" />
        </div>

        <FormErrors class="vfb-errors" :class="classes.errors" />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import FormLabel from './Label.ce.vue'
import FormErrors from './Errors.ce.vue';
import HelpText from './includes/HelpText.ce.vue';

interface Classes {
    wrapper?: string;
    container?: string;
    input?: string;
    label?: string;
    help?: string;
    errors?: string;
}

interface Props {
    id: string;
    label: string;
    name: string;
    required?: boolean;
    disabled?: boolean;
    helpText?: string;
    classes?: Classes;
    acceptTypes?: string;
    multiple?: boolean;
    maxFiles?: number;
    maxFileSizeKb?: string | number;
    dragAndDrop?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    required: false,
    disabled: false,
    helpText: '',
    classes: () => ({
        wrapper: 'form-group js-form-validation__group',
        container: 'position-relative',
        input: 'form-control',
        label: 'form-label',
        help: '',
        errors: '',
    }),
    acceptTypes: undefined,
    multiple: false,
    maxFiles: undefined,
    maxFileSizeKb: undefined,
    dragAndDrop: false,
});

// PHP collects several files of one input into an array only when the field
// name ends with "[]" — without it $_FILES keeps the LAST part only and every
// other selected file is silently dropped. Repeater members already arrive
// bracketed (`personen[0][cv]`) and simply get the suffix appended.
const submitName = computed(() => {
    if (!props.multiple) {
        return props.name
    }
    return props.name.endsWith('[]') ? props.name : `${props.name}[]`
})

const validationRules = computed(() => {
    const rules: Array<Record<string, number>> = []
    if (props.maxFiles !== null && props.maxFiles !== undefined) {
        rules.push({ maxFiles: Number(props.maxFiles) })
    }
    if (props.maxFileSizeKb !== null && props.maxFileSizeKb !== undefined) {
        rules.push({ maxFileSize: Number(props.maxFileSizeKb) })
    }
    return rules.length > 0 ? JSON.stringify(rules) : undefined
})

const emit = defineEmits<{
    (event: 'update:modelValue', value: File[] | null): void
}>()

const onChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (input && input.files) {
        emit('update:modelValue', Array.from(input.files));
    } else {
        emit('update:modelValue', null);
    }
}
</script>
